'use client'

import { usePathname } from 'next/navigation'
import { ThemeProvider, useTheme } from 'next-themes'
import { useEffect, useRef } from 'react'
import { LazyMotion, domAnimation, MotionConfig } from 'motion/react'
import { loadAnalytics } from '@/lib/analytics'
import { AppContext } from './app-context'

function usePrevious<T>(value: T) {
  const ref = useRef<T>(undefined)
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current
}

function ThemeWatcher() {
  const { resolvedTheme, setTheme } = useTheme()
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    function onMediaChange() {
      const systemTheme = media.matches ? 'dark' : 'light'
      if (resolvedTheme === systemTheme) {
        setTheme('system')
      }
    }
    onMediaChange()
    media.addEventListener('change', onMediaChange)
    return () => {
      media.removeEventListener('change', onMediaChange)
    }
  }, [resolvedTheme, setTheme])
  return null
}

export function Providers({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const previousPathname = usePrevious(pathname)
  return (
    <AppContext.Provider value={{ previousPathname }}>
      <ThemeProvider attribute="class" disableTransitionOnChange>
        <ThemeWatcher />
        <AnalyticsLoader />
        {/* Intentional: show animations to everyone regardless of the OS
            prefers-reduced-motion setting (owner's explicit choice for this
            personal site). */}
        <MotionConfig reducedMotion="never">
          <LazyMotion features={domAnimation} strict>
            {children}
          </LazyMotion>
        </MotionConfig>
      </ThemeProvider>
    </AppContext.Provider>
  )
}

// Warm the lazy PostHog loader during idle time so the library downloads off
// the critical path. Individual events still queue + flush if fired earlier.
function AnalyticsLoader() {
  useEffect(() => {
    const browserWindow = window as Window & {
      requestIdleCallback?: (cb: () => void) => number
      cancelIdleCallback?: (id: number) => void
    }

    if (browserWindow.requestIdleCallback) {
      const idleCallbackId = browserWindow.requestIdleCallback(() => {
        void loadAnalytics()
      })
      return () => browserWindow.cancelIdleCallback?.(idleCallbackId)
    }

    const timeoutId = browserWindow.setTimeout(() => void loadAnalytics(), 1)
    return () => browserWindow.clearTimeout(timeoutId)
  }, [])
  return null
}
